// Card Component
// ===============
// Updated for "frame" aesthetic (Feature #279):
// - White background with soft corners (rounded-xl = 20px)
// - Subtle border using frame-border color (#EDEDED)
// - Generous whitespace with increased padding
// - Elegant, subtle hover states
// Updated: Feature #283 - Added optional DesignLine accent

import React from 'react';
import DesignLine from './DesignLine';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  onClick?: () => void;
  /** Feature #283: Add DesignLine accent at top of card */
  showAccent?: boolean;
  style?: React.CSSProperties;
}

const Card: React.FC<CardProps> = ({
  children,
  className = '',
  padding = 'md',
  hover = false,
  onClick,
  showAccent = false,
  style,
}) => {
  // Increased padding for more generous whitespace (Feature #279)
  const paddingStyles = {
    none: '',
    sm: 'p-4',      // Was p-3, now p-4
    md: 'p-6',      // Was p-4, now p-6
    lg: 'p-8',      // Was p-6, now p-8
  };

  // Subtle and elegant hover states (Feature #279)
  const hoverStyles = hover
    ? 'hover:shadow-md hover:border-frame-border/80 dark:hover:border-frame-border-dark/80 cursor-pointer'
    : '';

  const clickableProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

  return (
    <div
      className={`
        bg-canvas dark:bg-canvas-dark
        rounded-xl
        border border-frame-border dark:border-frame-border-dark
        transition-all duration-200
        overflow-hidden
        ${hoverStyles}
        ${className}
      `}
      {...clickableProps}
      style={style}
    >
      {/* Feature #283: Optional DesignLine accent at top */}
      {showAccent && <DesignLine variant="top-accent" height={2} />}
      <div className={paddingStyles[padding]}>
        {children}
      </div>
    </div>
  );
};

export default Card;
